/**
 * The report context and the hook that reads it.
 *
 * Deliberately a module of its own, with no component in it: a file that
 * exports both a context and the component providing it is not a react-refresh
 * boundary, so editing it hands the already-mounted provider one context object
 * and a re-rendering child another — and the child, finding nothing in its own
 * copy, throws "used outside SupportReportProvider" on a tree that is correct.
 * Keeping the context here means a hot update can never split it.
 */
import { createContext, useContext } from "react";
import { CAPTURE_UI_ATTRIBUTE } from "./support-capture-engines";

export interface SupportReportContextValue {
	/** Opens the message box, empty — a picture is the reporter's to add. */
	openReport: () => void;
	/**
	 * Opens one conversation by id, whatever page the reporter is on.
	 *
	 * For the link in a notification mail: `/support/<id>` is staff ground, so
	 * a reporter following it is redirected — and the conversation they asked
	 * for opens in the panel instead of them landing on a dashboard with no
	 * idea what became of it.
	 */
	openConversation: (ticketId: number) => void;
	/** Replies the reporter has not read — drives the bubble's badge. */
	unreadCount: number;
	/** Collapses the panel and keeps the draft — closing is not discarding. */
	closeReport: () => void;
	isOpen: boolean;
}

export const SupportReportContext = createContext<SupportReportContextValue | null>(
	null
);

export const useSupportReport = (): SupportReportContextValue => {
	const value = useContext(SupportReportContext);
	if (!value) {
		throw new Error("useSupportReport used outside SupportReportProvider");
	}
	return value;
};

/** Marks the report UI's own nodes so a capture never contains them. */
export const CAPTURE_UI_PROPS: Record<string, string> = {
	[CAPTURE_UI_ATTRIBUTE]: "",
};
